import {WizardFocus} from './wizardFocus';
import {IWizardNode} from './wizardNode';

export interface INodeNavigationEngine {
    getNextNode(startNode?: WizardFocus): ng.IPromise<WizardFocus>;
    isIncluded(focus: WizardFocus): ng.IPromise<boolean>;
    /**
     * @description pass in a function(focus), and this will execute the function for each node that would be displayed, in order.
     * Returns a promise that is resolved when all nodes are tested
     */
    forEachIncludedNode(action: (focus: WizardFocus) => void): ng.IPromise<any>;
}

// why a factory?  I did not want other services to gain direct access to the navigation engine -- it's private to the navigation service that demands it
// BUT, I couldn't just new one up outside of angular, because it could need injections, so I abstracted it through a factory.
export class TreeNodeNavigationEngineFactory { 
    public static $inject = ['$injector', '$q', '$log'];
    constructor(private $injector: any,
        private $q: ng.IQService,
        private $log: ng.ILogService) { }

    public getInstance(nodeTemplate: IWizardNode[]): INodeNavigationEngine {
        return new TreeNodeNavigationEngine(this.$injector, this.$q, nodeTemplate, this.$log);
    }
}

// tslint:disable-next-line:max-classes-per-file
class TreeNodeNavigationEngine implements INodeNavigationEngine {
    private rootNode: IWizardNode;

    constructor(private $injector: any,
                private $q: ng.IQService,
                private nodeTemplate: IWizardNode[],
                private $log: ng.ILogService) {
        this.rootNode = {stateName: '', parent: undefined};
        this.configureNodeTree(nodeTemplate, this.rootNode);
    }
    
    /**
     * @description Return the next node that should be displayed, based upon the starting point described by startNode
     * @param startNode The node that we should start from in our evaluation.  This is usually the node that the wizard is currently focused on
     */
    public getNextNode(startNode?: WizardFocus): ng.IPromise<WizardFocus> {
        if (angular.isUndefined(startNode))
            startNode = new WizardFocus(this.rootNode);
        
        // bit of a hack here, but the state stored in node.subjectData is only meaningful during the course of a state transition
        // need to set it to the subject of the target focus
        if (startNode.node.subjectData)
            startNode.node.subjectData = startNode.subject;    

        return this.evaluateNextNode(startNode, startNode.node)
            .then((result: IWizardNode) => {
                return new WizardFocus(result);
            });
    }

    private evaluateNextNode(startNode: WizardFocus, node: IWizardNode): ng.IPromise<IWizardNode> {
        if (node === this.rootNode) {
            return this.evaluateNextNode(startNode, node.children[0]);
        }

        return this.isNodeIncluded(node)
            .then((included: boolean) => {
                if (included) {
                    // kind of an artificial function here so that I don't cram all the logic into this promise handler
                    return this.evaluateIncludedNode(startNode, node);
                }
                else {
                    let nextSiblingOrDefault = this.getNextSiblingOrDefault(node, node.parent);
                    if (nextSiblingOrDefault === this.rootNode) {
                        // in this case, we've looped through all remaining nodes in the wizard, and none are included
                        //  so just return the root node because we're at the end and that's what we do elsewhere
                        return this.$q.resolve(this.rootNode);
                    }
                    if (nextSiblingOrDefault === node.parent) {
                        // in this case, we've looped through all the nodes for this parent and none are included, so proceed to the next sibling of the parent
                        nextSiblingOrDefault = this.getNextSiblingOrDefault(node.parent, this.rootNode);
                    }
                    return this.evaluateNextNode(startNode, nextSiblingOrDefault);
                }
            });
    }

    // continue evaluation of "next" on a node that has indicated it is participating in the wizard
    private evaluateIncludedNode(startNode: WizardFocus, node: IWizardNode): ng.IPromise<IWizardNode> {
        // if the current node is a child of the node we are evaluating (node), 
        // it means we completed all child nodes, and are "popping" back up to the parent
        if (startNode && startNode.node.parent === node && angular.isArray(node.subjectData)) {
            return this.evaluateNextNode(startNode, this.repeatChildrenForNextSubject(node, startNode.node.subjectData));
        }

        // is the search being called for the start node -- common because when we start the search, we only know the node we are on
        if (startNode && startNode.isMatch(node)) {
            // if the node has children, it's time to step into the children
            if (angular.isUndefined(node.children) || node.children.length === 0)
                return this.evaluateNextNode(startNode, this.getNextSiblingOrDefault(node, node.parent));
            
            return this.evaluateNextNode(startNode, this.repeatChildrenForNextSubject(node));
        }

        // we landed on this node fair and square, if it is flagged for display (not a container only node, return it)
        // isDisplay only cancels display if it is actually defined and set to false -- it's an optional param, so if it's not defined, we assume it is true
        if (node.isDisplayNode !== false)
            return this.$q.when(node);   
        
        // we landed on this node for the first time, and it is not a display node, launch its children with the first subject record
        if (node.children && node.children.length > 0)
            return this.evaluateNextNode(startNode, this.repeatChildrenForNextSubject(node));
        
        // isDisplayNode is false, and the node does not have children, so get the next sibling
        return this.evaluateNextNode(startNode, this.getNextSiblingOrDefault(node, node.parent)); 
    }

    /**
     * @description used when we need to repeat a set of subnodes for a new record
     */
    private repeatChildrenForNextSubject(parentNode: IWizardNode, currentSubject?: any): IWizardNode {
        // if there are additional subjects in the parent node's collection, repeat the children
        // example:  we've navigated all of the child pages for location 1, now go to the first child page, but with location 2
        const subjects = parentNode.subjectData as any[];

        let index = 0;
        if (currentSubject) {
            index = subjects.indexOf(currentSubject);
            if (index < 0) {
                this.$log.error('Failed to find current focus in parent node.', parentNode);
                return undefined;
            }
            index++;
        }

        if (subjects && index < subjects.length) {
            // start the subset over, but with the next subject (eg, repeat the location pages, but move from location 1 to location 2)
            const result = parentNode.children[0];
            result.subjectData = subjects[index];
            return result;
        }
        else {
            // the current node has no more subjects 
            // example: we've iterated through all of the locations in the location node.  Move on to the next topic
            return this.getNextSiblingOrDefault(parentNode, parentNode.parent);
        }
    }

    private getNextSiblingOrDefault(node: IWizardNode, defaultNode: IWizardNode): IWizardNode {
        const parent = node.parent;

        if (angular.isUndefined(parent)) {
            this.$log.error('getNextSibling returned undefined because the treeNode had no parent.');
            return undefined;
        }

        // find the current node in the parent's collection
        let index = parent.children.indexOf(node);
        if (index < 0) {
            // if this happens, it's a bug.  this method finds siblings, so this node should ALWAYS be in its parent's child collection
            this.$log.error('getNextSibling was unable to find the current node in the parent node collection.  This is an error condition.', node);
            return undefined;
        }

        if (++index < parent.children.length) {
            // if there is a next sibling...
            const result = parent.children[index];
            // the next sibling should have the same focus as this one (so page 2 of a location will focus the same location as page 1)
            // may need to make this better in the future -- we're assuming that if the current node does not define its own subject provider function,
            // and if the node's parent does define a subject provider, that we want to use a record from teh parent's recordset
            // we are also assuming that the sibling has the current record (this allows us to avoid keeping state at the parent level, 
            // which causes problems with random- access to nodes
            if (angular.isUndefined(result.subjectProvider) && angular.isArray(parent.subjectData))
                result.subjectData = node.subjectData;
            return result;
        }
        // no more siblings, return the default node
        return defaultNode;
    }

    public isIncluded(focus: WizardFocus): ng.IPromise<boolean> {
        // if the isIncluded method is not defined, we assume that the wizard page is not conditional and should always be displayed.
        if (angular.isDefined(focus.node.isIncluded)) {
            // nodes can demand 'subject' and 'parent' to be injected into their isIncluded callbacks
            return this.$q.when(this.$injector.invoke(focus.node.isIncluded, undefined, { 'subject': focus.subject, 'parent': focus.node.parent}));
        }
        else {
            return this.$q.when(true);
        }
    }

    private isNodeIncluded(node: IWizardNode): ng.IPromise<boolean> {
        // if the isIncluded method is not defined, we assume that the wizard page is not conditional and should always be displayed.
        if (angular.isDefined(node.isIncluded)) {
            // nodes can demand 'subject' and 'parent' to be injected into their isIncluded callbacks
            return this.$q.when(this.$injector.invoke(node.isIncluded, undefined, { 'subject': node.subjectData, 'parent': node.parent}));
        }
        else {
            return this.$q.when(true);
        }
    }

    // initialize the tree nodes -- we don't surface the parent and child tree relationships in the config in order to keep the config simple.
    private configureNodeTree(nodeTemplate: IWizardNode[], parent: IWizardNode): void {
        parent.children = nodeTemplate;
        for (const node of nodeTemplate) {
            node.parent = parent;
            if (node.subjectProvider) {
                node.subjectData = this.$injector.invoke(node.subjectProvider);
            }

            if (angular.isArray(node.repeatedNodes) && node.repeatedNodes.length > 0) {
                this.configureNodeTree(node.repeatedNodes, node);
            }
        }
    }

    public forEachIncludedNode(action: (focus: WizardFocus) => void): ng.IPromise<any> {
        const recurse = (start: WizardFocus, current: WizardFocus): ng.IPromise<any> => {
            return this.getNextNode(current)
                .then((next: WizardFocus) => {
                    if (!start)
                        start = next;
                    else if (start.isEqual(next))
                        return;

                    action(next);
                    return recurse(start, next);
                });
        };
        
        return recurse(undefined, undefined);
    }
}
